import Inherit from '../../core/inherit';
import NavigationController from '../../core/navigation.controller';
import DataController from '../../data/data.controller';
import ForgotPasswordDelegate from './forgotPassword.delegate';
import { resolveCompany } from '../../data/central';
import { legacyCompany } from '../../company/session';
import {
    CODE_LENGTH,
    MESSAGES,
    PASSWORD_MAX,
    PASSWORD_MIN,
    RESTART_CODES,
    failureOutcome,
    isValidEmail,
    readOutcome,
    readTiming
} from './forgotPassword.helpers';

/**
 * "¿Olvidaste tu contraseña?": email → 6-digit code → new password → done.
 *
 * The email's company comes from central-service exactly as at sign-in (`resolveCompany`, with
 * Mary's classic login when it has no answer), but only for these requests: the session
 * company, cookie and storage are never touched, so a signed-in user keeps their session.
 * An email central-service doesn't know still gets the "si el correo existe…" step: the
 * request goes to Mary, whose API answers the same for every email.
 */
class ForgotPasswordController extends Inherit(NavigationController, ForgotPasswordDelegate) {
    data = new DataController();

    componentDidMount() {
        // Email typed on the login page (router state).
        const email = this.props.location?.state?.email;
        if (typeof email === 'string' && email.trim()) this.setState({ email: email.trim() });
        this.focusStep();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevState.step !== this.state.step) this.focusStep();
    }

    componentWillUnmount() {
        this.unmounted = true;
        this.stopCountdown();
    }

    /** Move focus to the first control of the step that just appeared. */
    focusStep() {
        const target = {
            email: this.emailRef,
            code: this.codeRef,
            password: this.passwordRef,
            done: this.doneRef
        }[this.state.step];
        target?.current?.focus?.();
    }

    /** setState that never runs after the page is gone. */
    update(state, callback) {
        if (!this.unmounted) this.setState(state, callback);
    }

    // -------- Requests --------

    async call(step, payload, company = this.state.company) {
        try {
            return readOutcome(await this.data.requestPasswordStep(step, company, payload));
        } catch (error) {
            return failureOutcome(error);
        }
    }

    /** The company whose API handles this email: central-service's answer, or Mary. */
    async resolveTarget(email) {
        const target = await resolveCompany(email);
        if (target.kind === 'company') return target;
        if (target.kind === 'legacy') return { kind: 'company', company: legacyCompany() };
        return target; // 'redirect' | 'elsewhere': a company another admin serves
    }

    // -------- Step 1: email --------

    async sendCode() {
        if (this.state.loading) return;
        const email = (this.state.email || '').trim();
        if (!isValidEmail(email)) {
            this.update({ error: MESSAGES.INVALID_EMAIL, notice: null });
            this.emailRef?.current?.focus?.();
            return;
        }

        this.update({ loading: true, error: null, notice: null, elsewhere: null, email });
        const target = await this.resolveTarget(email);
        if (target.kind !== 'company') {
            this.update({ loading: false, elsewhere: { companyName: target.companyName || '', url: target.url || null } });
            return;
        }

        const outcome = await this.call('forgot', { email }, target.company);
        if (!outcome.ok) {
            this.update({ loading: false, error: outcome.message });
            return;
        }
        const timing = readTiming(outcome.data);
        // Kept in memory only, for the verify / reset / resend requests of this email.
        this.update({ loading: false, step: 'code', code: '', company: target.company, expiresMinutes: timing.expiresMinutes });
        this.startCountdown(timing.resendAfterSeconds);
    }

    /** Step 2 → step 1, keeping the email. */
    changeEmail() {
        if (this.state.loading) return;
        this.stopCountdown();
        this.update({ step: 'email', code: '', error: null, notice: null, company: null });
    }

    /** The code can't be used any more: back to step 1 to ask for a new one. */
    restart(message) {
        this.stopCountdown();
        this.update({
            step: 'email',
            loading: false,
            code: '',
            password: '',
            confirm: '',
            error: null,
            notice: message
        });
    }

    // -------- Step 2: code --------

    async verifyCode(code = this.state.code) {
        if (this.state.loading) return;
        if (code.length !== CODE_LENGTH) {
            this.update({ error: MESSAGES.INCOMPLETE_CODE, codeRejected: true });
            this.codeRef?.current?.focus?.();
            return;
        }

        this.update({ loading: true, error: null });
        const outcome = await this.call('verify', { email: this.state.email, code });
        if (outcome.ok) {
            this.stopCountdown();
            this.update({ loading: false, step: 'password', code, password: '', confirm: '', submitted: false });
            return;
        }
        if (RESTART_CODES.includes(outcome.code)) {
            this.restart(outcome.message);
            return;
        }
        // Wrong code: empty the boxes for another try.
        this.update({ loading: false, code: '', error: outcome.message, codeRejected: outcome.code === 'INVALID_CODE' }, () =>
            this.codeRef?.current?.focus?.()
        );
    }

    async resendCode() {
        if (this.state.loading || this.state.resending || this.state.resendIn > 0) return;
        this.update({ resending: true, error: null });
        const outcome = await this.call('forgot', { email: this.state.email });
        if (!outcome.ok) {
            this.update({ resending: false, error: outcome.message });
            return;
        }
        const timing = readTiming(outcome.data);
        this.update({ resending: false, code: '', expiresMinutes: timing.expiresMinutes }, () => this.codeRef?.current?.focus?.());
        this.startCountdown(timing.resendAfterSeconds);
        this.props.onMessage?.('success', 'Te enviamos un nuevo código.');
    }

    startCountdown(seconds) {
        this.stopCountdown();
        // From a deadline, so a throttled background tab still counts real time.
        const deadline = Date.now() + seconds * 1000;
        const tick = () => {
            const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
            this.update({ resendIn: left });
            if (left === 0) this.stopCountdown();
        };
        tick();
        if (seconds > 0) this.countdown = setInterval(tick, 1000);
    }

    stopCountdown() {
        if (this.countdown) clearInterval(this.countdown);
        this.countdown = null;
    }

    // -------- Step 3: new password --------

    async resetPassword() {
        if (this.state.loading) return;
        const { email, code, password, confirm } = this.state;
        this.update({ submitted: true });
        if (password.length < PASSWORD_MIN || password.length > PASSWORD_MAX) {
            this.update({ error: null });
            this.passwordRef?.current?.focus?.();
            return;
        }
        if (password !== confirm) {
            this.confirmRef?.current?.focus?.();
            return;
        }

        this.update({ loading: true, error: null });
        const outcome = await this.call('reset', { email, code, password });
        if (outcome.ok) {
            this.update({ loading: false, step: 'done', code: '', password: '', confirm: '' });
            return;
        }
        if (RESTART_CODES.includes(outcome.code)) {
            this.restart(outcome.message);
            return;
        }
        if (outcome.code === 'INVALID_CODE') {
            // The verified code was replaced meanwhile (e.g. a resend from another tab).
            this.update({ loading: false, step: 'code', code: '', error: outcome.message, codeRejected: true });
            return;
        }
        this.update({ loading: false, error: outcome.message }, () => this.passwordRef?.current?.focus?.());
    }

    // -------- Navigation --------

    /** Form submit (button or Enter) of the current step. */
    submit(e) {
        e?.preventDefault?.();
        if (this.state.step === 'email') this.sendCode();
        else if (this.state.step === 'code') this.verifyCode();
        else if (this.state.step === 'password') this.resetPassword();
    }

    goToLogin() {
        this.props.navigate('/login', { state: { email: this.state.email } });
    }
}

export default ForgotPasswordController;
